import React, { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useScopedPersistenceInit, useUser, useUserAuth } from '@features/user';
import { useEventTracking } from '@services/statsig';

import { setAuth0AccessTokenFunction } from '../store/auth0BaseQuery';

/**
 * Component that handles all Auth0-related initialization:
 * 1. Sets up the Auth0 token function for RTK Query
 * 2. Initializes user authentication (runs once per app lifecycle)
 * 3. Initializes scoped persistence after getUserInfo completes
 * This should be placed high in the component tree, after Auth0Provider
 */
export const Auth0Initializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, user } = useAuth0();
  const { initAuth } = useUserAuth();
  const { userState } = useUser();
  const { auth: authMetrics } = useEventTracking();
  const userInitializationRef = useRef(false);
  const authTrackingRef = useRef(false);

  // Initialize scoped persistence after getUserInfo completes
  useScopedPersistenceInit();

  // Initialize RTK Query token function
  // This sets the token function for both auth0BaseQuery and streamingApi
  // (streamingApi now uses the shared function from auth0BaseQuery)
  useEffect(() => {
    if (isAuthenticated && getAccessTokenSilently) {
      // Set the token function for the auth0BaseQuery to use
      // We need to cast this because the type definitions from useAuth0 might
      // differ slightly from what we're using in auth0BaseQuery
      setAuth0AccessTokenFunction(
        getAccessTokenSilently as Parameters<typeof setAuth0AccessTokenFunction>[0],
      );
    }
  }, [isAuthenticated, getAccessTokenSilently]);

  // Initialize user authentication (once per app lifecycle)
  useEffect(() => {
    // Only initialize if:
    // 1. Auth0 has finished loading
    // 2. User is authenticated
    // 3. We haven't already initialized
    if (!isLoading && isAuthenticated && !userInitializationRef.current) {
      userInitializationRef.current = true;
      initAuth();

      // Track authentication event (once per session)
      if (!authTrackingRef.current) {
        authTrackingRef.current = true;

        // Determine if this is a new signup or returning login
        // Check multiple indicators:
        // 1. Onboarding not completed
        // 2. First login time is very recent (within 5 minutes)
        // 3. Signup markers in localStorage (referral code, group invite)
        const hasSignupMarkers =
          localStorage.getItem('referralCode') !== null || localStorage.getItem('action') !== null;
        const isRecentFirstLogin =
          userState?.firstLoginTime && Date.now() - userState.firstLoginTime < 300000;
        const isNewUser =
          !userState?.onboardingSteps?.account_creation_onboarding_completed ||
          isRecentFirstLogin ||
          hasSignupMarkers;

        if (isNewUser) {
          // Track signup completed for new users
          authMetrics.signupCompleted({
            auth_method: 'auth0',
            ...(user?.email && { user_email: user.email }),
            ...(user?.sub && { auth0_sub: user.sub }),
            has_referral: !!localStorage.getItem('referralCode'),
            has_group_invite: !!localStorage.getItem('action'),
          });
        } else {
          // Track login completed for returning users
          authMetrics.loginCompleted({
            auth_method: 'auth0',
            is_returning_user: true,
            ...(user?.email && { user_email: user.email }),
            ...(user?.sub && { auth0_sub: user.sub }),
          });
        }
      }
    }
  }, [isLoading, isAuthenticated, initAuth, authMetrics, user, userState]);

  return <>{children}</>;
};
